import React, { useState, useMemo, useEffect } from 'react';
import {
  settingsConfig,
  villageSettingsConfig,
} from '../../consts/other.conts';
import classes from './styles.module.scss';
import { useTranslation } from 'react-i18next';
import { ChevronRightIcon, SaveIcon } from '../../assets/icons';
import { useUser } from '../../contexts/user.context';
import { KatoType } from '../../types/kato.types';
import { ToastContainer } from 'react-toastify';
interface TreeNode {
  key: string;
  childtype?: string;
  children?: TreeNode[];
}

const flattenKeys = (node: TreeNode): string[] => {
  let keys = [node.key];
  if (node.children) {
    for (const child of node.children) {
      keys = keys.concat(flattenKeys(child));
    }
  }
  return keys;
};

const collectParentKeys = (
  config: TreeNode[],
  targetKey: string,
  path: string[] = [],
): string[] | null => {
  for (const item of config) {
    if (item.key === targetKey) return path;
    if (item.children) {
      const result = collectParentKeys(item.children, targetKey, [
        ...path,
        item.key,
      ]);
      if (result) return result;
    }
  }
  return null;
};

const findNodeByKey = (nodes: TreeNode[], key: string): TreeNode | null => {
  for (const node of nodes) {
    if (node.key === key) return node;
    if (node.children) {
      const found = findNodeByKey(node.children, key);
      if (found) return found;
    }
  }
  return null;
};

const baseClass = 'settings';
const translationKey = 'settings-config';

const tabs = [
  {
    key: KatoType.OBLAST,
    label: `${translationKey}.oblast`,
  },
  {
    key: KatoType.VILLAGE,
    label: `${translationKey}.village`,
  },
];

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const [hoverPath, setHoverPath] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const { modules, handleUpdateModules, moduleData } = useUser();
  const [selectedTab, setSelectedTab] = useState<KatoType>(KatoType.OBLAST);
  const [config, setConfig] = useState<TreeNode[]>(settingsConfig);

  const getLevelItems = (level: number): TreeNode[] => {
    let items = config as TreeNode[];
    for (let i = 0; i < level; i++) {
      const node = items.find((n) => n.key === hoverPath[i]);
      if (!node || !node.children) return [];
      items = node.children;
    }
    return items;
  };

  const updateParentSelection = (key: string, keys: Set<string>) => {
    const parents = collectParentKeys(config, key);
    if (!parents) return;

    for (let i = parents.length - 1; i >= 0; i--) {
      const parentKey = parents[i];
      const parentNode = findNodeByKey(config, parentKey);
      if (!parentNode || !parentNode.children) continue;

      const allUnchecked = parentNode.children.every(
        (child) => !keys.has(child.key),
      );
      if (allUnchecked) {
        keys.delete(parentKey);
      } else {
        keys.add(parentKey);
      }
    }
  };

  const toggleNode = (node: TreeNode, checked: boolean) => {
    let keys = new Set<string>();

    if (node.childtype === 'radio') {
      if (checked) {
        keys.add(node.key);
        const parents = collectParentKeys(config, node.key);
        if (parents) {
          parents.forEach((k) => keys.add(k));
        }
      }
    } else {
      keys = new Set(selectedKeys);
      const nodeKeys = flattenKeys(node);

      if (checked) {
        nodeKeys.forEach((k) => keys.add(k));

        const parents = collectParentKeys(config, node.key);
        if (parents) {
          parents.forEach((k) => keys.add(k));
        }
      } else {
        nodeKeys.forEach((k) => keys.delete(k));
      }

      updateParentSelection(node.key, keys);
    }
    setSelectedKeys(keys);
  };

  const isChecked = (node: TreeNode): boolean => selectedKeys.has(node.key);

  const levels = useMemo(() => {
    const result: TreeNode[][] = [];
    for (let i = 0; i <= hoverPath.length; i++) {
      const items = getLevelItems(i);
      if (items.length > 0) result.push(items);
    }
    return result;
  }, [hoverPath, config]);

  const handleSave = () => {
    handleUpdateModules({
      ...moduleData,
      [selectedTab.toString()]: Array.from(selectedKeys),
    });
  };

  useEffect(() => {
    if (hoverPath.length === 0 && config.length > 0) {
      const firstPath: string[] = [];
      let node: any = config[0];
      while (node) {
        firstPath.push(node.key);
        node = node.children?.[0] || null;
      }
      setHoverPath(firstPath);
    }
  }, [hoverPath, config]);

  useEffect(() => {
    const tab = selectedTab.toString() as unknown as
      | KatoType.OBLAST
      | KatoType.VILLAGE;
    const saved = moduleData[tab] ?? [];
    setSelectedKeys(new Set(saved));
  }, [modules, selectedTab, moduleData]);

  useEffect(() => {
    const cnf =
      selectedTab === KatoType.OBLAST ? settingsConfig : villageSettingsConfig;
    setConfig(cnf);
  }, [selectedTab]);

  return (
    <div className={classes[baseClass]}>
      <div className={classes[`${baseClass}__actions`]}>
        <button onClick={handleSave}>
          {' '}
          <SaveIcon /> {t('actions.save')}
        </button>
      </div>

      <div className={classes[`${baseClass}__actions`]}>
        {tabs.map((tab) => (
          <div
            key={tab.key}
            className={`${classes[`${baseClass}__tab`]} ${selectedTab === tab.key ? classes[`${baseClass}__tab--active`] : ''}`}
            onClick={() => setSelectedTab(tab.key)}
          >
            {t(tab.label)}
          </div>
        ))}
      </div>

      <div className={classes[`${baseClass}__grid`]}>
        {levels.map((items, levelIndex) => (
          <div key={levelIndex} className={classes[`${baseClass}__column`]}>
            {items.map((item) => (
              <div
                key={item.key}
                onMouseEnter={() => {
                  const newPath = [...hoverPath.slice(0, levelIndex), item.key];
                  setHoverPath(newPath);
                }}
                className={`${classes[`${baseClass}__item`]} ${hoverPath.includes(item.key) ? classes[`${baseClass}__item--hover`] : ''}`}
              >
                {item.childtype === 'radio' ? (
                  <label>
                    <input
                      type="radio"
                      checked={isChecked(item)}
                      onChange={(e) => toggleNode(item, e.target.checked)}
                    />
                    <span>
                      {t(`${translationKey}.${item.key}`, { point: '' })}
                    </span>
                    {item.children && item.children.length > 0 && (
                      <ChevronRightIcon />
                    )}
                  </label>
                ) : (
                  <label>
                    <input
                      type="checkbox"
                      checked={isChecked(item)}
                      onChange={(e) => toggleNode(item, e.target.checked)}
                    />
                    <span>
                      {t(`${translationKey}.${item.key}`, { point: '' })}
                    </span>
                    {item.children && item.children.length > 0 && (
                      <ChevronRightIcon />
                    )}
                  </label>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
      <ToastContainer autoClose={2000} />
    </div>
  );
};

export default Settings;
